<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { Head } from "@inertiajs/vue3"
import { GroupedBar } from "@unovis/ts"
import {
  VisAxis,
  VisGroupedBar,
  VisXYContainer,
} from "@unovis/vue"
import { Clock, DollarSign, ExternalLink, FileText } from "lucide-vue-next"
import { computed } from "vue"

import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppTooltip from "@/components/AppTooltip.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import StatusBadge from "@/components/applications/StatusBadge.vue"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart"
import AppLayout from "@/layouts/AppLayout.vue"
import { dashboardPath, editLoanApplicationPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

// ─── Props ────────────────────────────────────────────────────────────────────

interface MonthlyApp  { month: string; count: number; funded_count: number }
interface MonthlyVal  { month: string; total: number; funded_total: number }
interface StatusDist  { label: string; status: string; count: number; color: string }
interface TypeDist    { label: string; count: number; color: string }
interface InProgress  { label: string; status: string; count: number; color: string }
interface RecentApp {
  id: string
  customer: string
  loan_type: string
  amount: number
  status: string
  partner: string
  created_at: string
}

type Period = "week" | "month" | "quarter"

const props = defineProps<{
  period: Period
  stats: {
    apps_count: number
    apps_funded: number
    apps_change: number | null
    value_total: number
    value_funded: number
    value_change: number | null
  }
  in_progress_breakdown: InProgress[]
  monthly_applications: MonthlyApp[]
  monthly_loan_values: MonthlyVal[]
  status_distribution: StatusDist[]
  loan_type_distribution: TypeDist[]
  recent_applications: RecentApp[]
}>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: dashboardPath() },
]

// ─── Period filter ────────────────────────────────────────────────────────────

const periods: { value: Period; label: string }[] = [
  { value: "week",    label: "Last 7 Days" },
  { value: "month",   label: "Last 30 Days" },
  { value: "quarter", label: "Last 90 Days" },
]

const periodLabel = computed(
  () => periods.find((p) => p.value === props.period)?.label ?? "Last 30 Days",
)

function setPeriod(p: Period) {
  router.visit(dashboardPath(), {
    data: { period: p },
    preserveScroll: true,
    preserveState: true,
  })
}

// ─── Derived ──────────────────────────────────────────────────────────────────

const inProgressTotal = computed(() =>
  props.in_progress_breakdown.reduce((s, x) => s + x.count, 0),
)

const totalApps = computed(() =>
  props.status_distribution.reduce((s, x) => s + x.count, 0),
)

const totalLoanTypes = computed(() =>
  props.loan_type_distribution.reduce((s, x) => s + x.count, 0),
)

type MonthlyData = MonthlyApp

// Sorted largest → smallest for ranked bar lists
const sortedStatusDist = computed(() =>
  [...props.status_distribution].sort((a, b) => b.count - a.count),
)
const sortedLoanTypeDist = computed(() =>
  [...props.loan_type_distribution].sort((a, b) => b.count - a.count),
)

// ─── Bar chart ────────────────────────────────────────────────────────────────

const barChartConfig = {
  count: { label: "Applications", color: "#22c55e" },
} satisfies ChartConfig

// ─── Tooltip helpers ──────────────────────────────────────────────────────────

function tooltipBox(content: string) {
  return `<div style="background:rgba(15,23,42,0.82);backdrop-filter:blur(6px);border:1px solid rgba(255,255,255,0.12);border-radius:8px;padding:8px 12px;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);color:#f1f5f9;">${content}</div>`
}

const barTooltipTriggers = {
  [GroupedBar.selectors.bar]: (d: MonthlyData) =>
    tooltipBox(
      `<div style="color:#94a3b8;margin-bottom:4px">${d.month}</div>
     <div style="display:flex;align-items:center;gap:6px">
       <span style="width:8px;height:8px;border-radius:2px;background:#22c55e;display:inline-block"></span>
       <span style="font-weight:500">${d.count} Applications</span>
     </div>`,
    ),
}

// ─── Sparklines ───────────────────────────────────────────────────────────────

const SPARK_W = 80, SPARK_H = 28

function sparklineLine(values: number[]) {
  if (values.length < 2) return ""
  const max = Math.max(...values),
    min = Math.min(...values),
    range = max - min || 1
  const stepX = SPARK_W / (values.length - 1)
  return values
    .map(
      (v, i) =>
        `${i === 0 ? "M" : "L"} ${+(i * stepX).toFixed(1)} ${+(SPARK_H - ((v - min) / range) * (SPARK_H - 6) - 3).toFixed(1)}`,
    )
    .join(" ")
}
function sparklineArea(values: number[]) {
  if (values.length < 2) return ""
  return `${sparklineLine(values)} L ${SPARK_W} ${SPARK_H} L 0 ${SPARK_H} Z`
}

const appSpark = computed(() => props.monthly_applications.map((m) => m.count))
const loanSpark = computed(() => props.monthly_loan_values.map((m) => m.total))

// ─── Formatting ───────────────────────────────────────────────────────────────

function formatAUD(n: number) {
  if (n >= 1_000_000)
    return "$" + new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1 }).format(n / 1_000_000) + "M"
  if (n >= 1_000)
    return "$" + new Intl.NumberFormat("en-AU", { maximumFractionDigits: 0 }).format(n / 1_000) + "K"
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(n)
}

function formatAUDFull(n: number) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(n)
}

function formatCount(n: number) {
  return new Intl.NumberFormat("en-AU").format(n)
}

function pct(count: number, total: number) {
  if (total === 0) return "0%"
  const p = (count / total) * 100
  return p < 0.1 ? "<0.1%" : `${p.toFixed(1)}%`
}

function changeLabel(pct: number | null): { text: string; positive: boolean | null } {
  if (pct === null) return { text: "—", positive: null }
  return { text: `${pct >= 0 ? "↑" : "↓"} ${Math.abs(pct)}%`, positive: pct >= 0 }
}

const recentAppColumns: TableColumn[] = [
  { key: "customer", label: "Customer" },
  { key: "type", label: "Type" },
  { key: "partner", label: "Partner" },
  { key: "amount", label: "Amount", align: "right" },
  { key: "status", label: "Status" },
  { key: "submitted", label: "Submitted" },
  { key: "link", label: "" },
]
</script>

<template>
  <Head title="Dashboard" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 p-4">

      <!-- ── Period filter ─────────────────────────────────────────────────── -->
      <div class="flex items-center justify-end">
        <div class="bg-muted flex rounded-lg p-1 gap-1">
          <button
            v-for="p in periods"
            :key="p.value"
            @click="setPeriod(p.value)"
            :class="[
              'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              period === p.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            ]"
          >
            {{ p.label }}
          </button>
        </div>
      </div>

      <!-- ── Stat cards ──────────────────────────────────────────────────── -->
      <div class="grid gap-4 md:grid-cols-3">

        <!-- Applications -->
        <Card>
          <CardHeader class="pb-2">
            <div class="flex items-start justify-between">
              <CardTitle class="text-muted-foreground text-sm font-medium">Applications — {{ periodLabel }}</CardTitle>
              <div class="bg-primary/10 text-primary rounded-lg p-2">
                <FileText class="size-4" />
              </div>
            </div>
          </CardHeader>
          <CardContent class="pt-0">
            <div class="flex items-end justify-between gap-4">
              <div class="flex-1">
                <p class="text-4xl font-bold tracking-tight">{{ stats.apps_count }}</p>
                <p class="text-muted-foreground mt-0.5 text-xs">
                  <span
                    :class="changeLabel(stats.apps_change).positive === true ? 'text-green-500' :
                             changeLabel(stats.apps_change).positive === false ? 'text-red-500' :
                             'text-muted-foreground'"
                    class="font-medium"
                  >{{ changeLabel(stats.apps_change).text }}</span>
                  vs previous period
                </p>
                <div class="mt-3 rounded-md bg-green-500/10 px-2.5 py-1.5">
                  <p class="text-xs text-green-700 dark:text-green-400 font-medium">Funded</p>
                  <p class="text-xl font-bold text-green-600 dark:text-green-400 leading-tight">{{ stats.apps_funded }}</p>
                </div>
              </div>
              <svg :width="SPARK_W" :height="SPARK_H" class="shrink-0 opacity-70 self-start mt-1">
                <defs>
                  <linearGradient id="sg1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#3b82f6" stop-opacity="0.3" />
                    <stop offset="100%" stop-color="#3b82f6" stop-opacity="0" />
                  </linearGradient>
                </defs>
                <path :d="sparklineArea(appSpark)" fill="url(#sg1)" />
                <path :d="sparklineLine(appSpark)" fill="none" stroke="#3b82f6" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </CardContent>
        </Card>

        <!-- In Progress -->
        <Card>
          <CardHeader class="pb-2">
            <div class="flex items-start justify-between">
              <CardTitle class="text-muted-foreground text-sm font-medium">In Progress</CardTitle>
              <div class="rounded-lg bg-yellow-500/10 p-2 text-yellow-500">
                <Clock class="size-4" />
              </div>
            </div>
          </CardHeader>
          <CardContent class="pt-0">
            <p class="text-4xl font-bold tracking-tight">{{ inProgressTotal }}</p>
            <p class="text-muted-foreground mt-1 mb-3 text-xs">active applications</p>
            <div class="flex flex-col gap-1.5">
              <div
                v-for="item in in_progress_breakdown"
                :key="item.status"
                class="flex items-center gap-2 text-xs"
              >
                <span class="text-muted-foreground w-14 shrink-0">{{ item.label }}</span>
                <AppTooltip side="top" :delay-duration="100">
                  <div class="bg-muted h-1.5 flex-1 cursor-default overflow-hidden rounded-full">
                    <div
                      class="h-full rounded-full"
                      :style="{
                        width: inProgressTotal > 0 ? `${(item.count / inProgressTotal) * 100}%` : '0%',
                        background: item.color,
                      }"
                    />
                  </div>
                  <template #content>
                    <span class="font-medium">{{ pct(item.count, inProgressTotal) }}</span>
                    <span class="text-muted-foreground ml-1">of in-progress</span>
                  </template>
                </AppTooltip>
                <span class="w-4 shrink-0 text-right font-medium">{{ item.count }}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <!-- Loan Value -->
        <Card>
          <CardHeader class="pb-2">
            <div class="flex items-start justify-between">
              <CardTitle class="text-muted-foreground text-sm font-medium">Loan Value — {{ periodLabel }}</CardTitle>
              <div class="rounded-lg bg-green-500/10 p-2 text-green-500">
                <DollarSign class="size-4" />
              </div>
            </div>
          </CardHeader>
          <CardContent class="pt-0">
            <div class="flex items-end justify-between gap-4">
              <div class="flex-1">
                <p class="text-4xl font-bold tracking-tight">{{ formatAUD(stats.value_total) }}</p>
                <p class="text-muted-foreground mt-0.5 text-xs">
                  <span
                    :class="changeLabel(stats.value_change).positive === true ? 'text-green-500' :
                             changeLabel(stats.value_change).positive === false ? 'text-red-500' :
                             'text-muted-foreground'"
                    class="font-medium"
                  >{{ changeLabel(stats.value_change).text }}</span>
                  vs previous period
                </p>
                <div class="mt-3 rounded-md bg-green-500/10 px-2.5 py-1.5">
                  <p class="text-xs text-green-700 dark:text-green-400 font-medium">Funded</p>
                  <p class="text-xl font-bold text-green-600 dark:text-green-400 leading-tight">{{ formatAUD(stats.value_funded) }}</p>
                </div>
              </div>
              <svg :width="SPARK_W" :height="SPARK_H" class="shrink-0 opacity-70 self-start mt-1">
                <defs>
                  <linearGradient id="sg2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#22c55e" stop-opacity="0.3" />
                    <stop offset="100%" stop-color="#22c55e" stop-opacity="0" />
                  </linearGradient>
                </defs>
                <path :d="sparklineArea(loanSpark)" fill="url(#sg2)" />
                <path :d="sparklineLine(loanSpark)" fill="none" stroke="#22c55e" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
          </CardContent>
        </Card>
      </div>

      <!-- ── Overview card ──────────────────────────────────────────────── -->
      <Card class="gap-0 p-0">
        <CardHeader class="border-b px-6 py-4">
          <CardTitle>Overview</CardTitle>
        </CardHeader>

        <CardContent class="p-0">
          <div class="grid border-b md:grid-cols-7">

            <!-- Bar chart — 4 cols -->
            <div class="border-border/60 p-6 md:col-span-4 md:border-r">
              <p class="mb-1 text-sm font-medium">Monthly Applications</p>
              <p class="text-muted-foreground mb-4 text-xs">Last 6 months</p>
              <ChartContainer :config="barChartConfig" class="h-64 w-full">
                <VisXYContainer :data="monthly_applications">
                  <VisGroupedBar
                    :x="(_d: MonthlyData, i: number) => i"
                    :y="[(d: MonthlyData) => d.count]"
                    :color="[barChartConfig.count.color]"
                    :rounded-corners="4"
                    :bar-padding="0.3"
                  />
                  <VisAxis
                    type="x"
                    :num-ticks="6"
                    :tick-format="(i: number) => monthly_applications[i]?.month ?? ''"
                  />
                  <ChartTooltip :triggers="barTooltipTriggers" />
                </VisXYContainer>
              </ChartContainer>
            </div>

            <!-- Ranked bar lists — 3 cols, split 50/50 -->
            <div class="grid md:col-span-3 md:grid-cols-2">

              <!-- Application Status -->
              <div class="border-border/60 p-5 md:border-r">
                <p class="mb-1 text-sm font-medium">Application Status</p>
                <p class="text-muted-foreground mb-4 text-xs">All time — {{ formatCount(totalApps) }} total</p>
                <div class="flex flex-col gap-2.5">
                  <div
                    v-for="seg in sortedStatusDist"
                    :key="seg.status"
                    class="group flex flex-col gap-1"
                  >
                    <div class="flex items-center justify-between text-xs">
                      <div class="flex items-center gap-1.5">
                        <span class="size-2 shrink-0 rounded-full" :style="{ background: seg.color }" />
                        <span class="text-muted-foreground">{{ seg.label }}</span>
                      </div>
                      <span class="font-medium tabular-nums">{{ formatCount(seg.count) }}</span>
                    </div>
                    <AppTooltip side="top" :delay-duration="100">
                      <div class="bg-muted h-2 w-full cursor-default overflow-hidden rounded-full">
                        <div
                          class="h-full rounded-full transition-all duration-500"
                          :style="{
                            width: totalApps > 0 ? `${(seg.count / totalApps) * 100}%` : '0%',
                            background: seg.color,
                          }"
                        />
                      </div>
                      <template #content>
                        <span class="font-medium">{{ pct(seg.count, totalApps) }}</span>
                        <span class="text-muted-foreground ml-1">of total</span>
                      </template>
                    </AppTooltip>
                  </div>
                </div>
              </div>

              <!-- Application Type -->
              <div class="p-5">
                <p class="mb-1 text-sm font-medium">Application Type</p>
                <p class="text-muted-foreground mb-4 text-xs">All time — {{ formatCount(totalLoanTypes) }} total</p>
                <div class="flex flex-col gap-2.5">
                  <div
                    v-for="seg in sortedLoanTypeDist"
                    :key="seg.label"
                    class="group flex flex-col gap-1"
                  >
                    <div class="flex items-center justify-between text-xs">
                      <div class="flex items-center gap-1.5">
                        <span class="size-2 shrink-0 rounded-full" :style="{ background: seg.color }" />
                        <span class="text-muted-foreground">{{ seg.label }}</span>
                      </div>
                      <span class="font-medium tabular-nums">{{ formatCount(seg.count) }}</span>
                    </div>
                    <AppTooltip side="top" :delay-duration="100">
                      <div class="bg-muted h-2 w-full cursor-default overflow-hidden rounded-full">
                        <div
                          class="h-full rounded-full transition-all duration-500"
                          :style="{
                            width: totalLoanTypes > 0 ? `${(seg.count / totalLoanTypes) * 100}%` : '0%',
                            background: seg.color,
                          }"
                        />
                      </div>
                      <template #content>
                        <span class="font-medium">{{ pct(seg.count, totalLoanTypes) }}</span>
                        <span class="text-muted-foreground ml-1">of total</span>
                      </template>
                    </AppTooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Applications -->
          <div class="p-6">
            <p class="mb-4 text-sm font-medium">Recent Applications</p>
            <AppDataTable :columns="recentAppColumns" compact>
              <tr
                v-for="app in recent_applications"
                :key="app.id"
                class="border-border hover:bg-muted/40 border-b transition-colors last:border-0"
              >
                <td class="px-4 py-3 font-medium">{{ app.customer }}</td>
                <td class="text-muted-foreground px-4 py-3">{{ app.loan_type }}</td>
                <td class="text-muted-foreground px-4 py-3 text-xs">{{ app.partner }}</td>
                <td class="px-4 py-3 text-right font-medium tabular-nums">{{ formatAUDFull(app.amount) }}</td>
                <td class="px-4 py-3"><StatusBadge :status="app.status" /></td>
                <td class="text-muted-foreground px-4 py-3 text-xs">
                  <RelativeTime :date="app.created_at" />
                </td>
                <td class="px-4 py-3">
                  <a
                    :href="editLoanApplicationPath(app.id)"
                    class="text-muted-foreground hover:text-foreground inline-flex transition-colors"
                  >
                    <ExternalLink class="size-3.5" />
                  </a>
                </td>
              </tr>
            </AppDataTable>
          </div>
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
